import {
  css,
  Icon16,
  ModalBareboneProps,
  styled,
  Text,
  Tooltip,
} from '@zeiss/beyond-online-react';
import {
  GeneralModal,
  ModalText,
  StyledContainer,
} from '@zeiss/lib-components';
import { useBreakpoint } from '@zeiss/lib-helpers';
import { Trans, useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { ApplicationsChangeData } from './';
import { showToast } from 'src/helpers/showToast';
import { useUpdateApplicationListApplications } from 'src/hooks/api';
import { StandardRouteParams } from 'src/types';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-responsive-s);
  margin-top: var(--spacing-l);
`;

const ChangesItem = styled.div<StyledContainer>`
  display: flex;
  align-items: center;
  justify-content: space-between;

  ${({ $isMinTablet }) =>
    /* istanbul ignore next -- @preserve */
    $isMinTablet
      ? css`
          flex-direction: row;
        `
      : css`
          flex-direction: column;
          align-items: flex-start;
        `}

  & p {
    margin: 0;
  }
`;

const AppTitle = styled(Text)<StyledContainer>`
  font: ${({ $isMinTablet }) =>
    /* istanbul ignore next -- @preserve */
    $isMinTablet ? 'var(--text-body-m)' : 'var(--text-body-s)'};
  font-weight: bold;
`;

const ChangesContent = styled.div<StyledContainer>`
  display: flex;
  gap: var(--spacing-xs);
  align-items: center;
  justify-content: space-between;
  width: ${({ $isMinTablet }) =>
    /* istanbul ignore next -- @preserve */
    $isMinTablet ? 'auto' : '100%'};
`;

const ChangeText = styled(Text)<{ $color: string; $isMinTablet: boolean }>`
  font: ${({ $isMinTablet }) =>
    /* istanbul ignore next -- @preserve */
    $isMinTablet ? 'var(--text-body-m)' : 'var(--text-body-s)'};
  color: ${({ $color }) => $color};
`;

const IconContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

interface UpdateApplicationsModalProps extends Pick<
  ModalBareboneProps,
  'isOpen' | 'onClose'
> {
  data: ApplicationsChangeData & {
    name: string;
  };
}

export const UpdateApplicationsModal = ({
  data: { addedApplications, name, removedApplications },
  isOpen,
  onClose,
}: UpdateApplicationsModalProps) => {
  const { t } = useTranslation(['common']);
  const { applicationListId } = useParams<StandardRouteParams>();
  const isMinTablet = useBreakpoint('tablet');

  const { isPending, mutate: updateApplications } =
    useUpdateApplicationListApplications(applicationListId!);

  const saveChanges = () => {
    updateApplications(
      {
        idsToAdd: addedApplications.map(({ id }) => id),
        idsToRemove: removedApplications.map(({ id }) => id),
      },
      {
        onError: () => {
          showToast({
            props: {
              title: t('common:notification.errorX', {
                action: t('common:action.updated'),
                x: t('common:glossary.applicationList'),
              }),
              type: 'error',
            },
          });
        },
        onSuccess: () => {
          showToast({
            props: {
              title: t('common:notification.successX', {
                action: t('common:action.updated'),
                x: t('common:glossary.applicationList'),
              }),
              type: 'success',
            },
          });

          onClose?.();
        },
      },
    );
  };

  return (
    <GeneralModal
      acceptButtonText={t('common:action.confirmAndSave')}
      declineButtonText={t('common:action.cancel')}
      headline={t('common:title.xChanged', {
        x: t('common:glossary.application_other'),
      })}
      isOpen={isOpen}
      loading={isPending}
      onAccept={saveChanges}
      onClose={onClose}
      onDecline={onClose}
      size="m"
    >
      <ModalText>
        <Trans
          i18nKey="common:text.applicationsChanging"
          t={t}
          values={{ x: name }}
        >
          <b />
        </Trans>
      </ModalText>
      <Container>
        {removedApplications.map(({ id, name }) => (
          <ChangesItem $isMinTablet={isMinTablet} key={id}>
            <AppTitle $isMinTablet={isMinTablet}>{name}</AppTitle>
            <ChangesContent $isMinTablet={isMinTablet}>
              <ChangeText
                $color="var(--color-global-text-error)"
                $isMinTablet={isMinTablet}
              >
                {t('common:text.removingFromApplicationList')}
              </ChangeText>
              <Tooltip
                anchorEl={
                  <IconContainer>
                    <Icon16
                      fill="var(--color-global-icon-subtlest)"
                      icon="Help"
                    />
                  </IconContainer>
                }
                fitContent
                position={isMinTablet ? 'bottom' : 'left'}
              >
                {t('common:tooltip.removingFromApplicationList')}
              </Tooltip>
            </ChangesContent>
          </ChangesItem>
        ))}

        {addedApplications.map(({ id, name }) => (
          <ChangesItem $isMinTablet={isMinTablet} key={id}>
            <AppTitle $isMinTablet={isMinTablet}>{name}</AppTitle>
            <ChangesContent $isMinTablet={isMinTablet}>
              <ChangeText
                $color="var(--color-global-text-success)"
                $isMinTablet={isMinTablet}
              >
                {t('common:text.addingToApplicationList')}
              </ChangeText>
              <Tooltip
                anchorEl={
                  <IconContainer>
                    <Icon16
                      fill="var(--color-global-icon-subtlest)"
                      icon="Help"
                    />
                  </IconContainer>
                }
                fitContent
                position={isMinTablet ? 'bottom' : 'left'}
              >
                {t('common:tooltip.addingToApplicationList')}
              </Tooltip>
            </ChangesContent>
          </ChangesItem>
        ))}
      </Container>
    </GeneralModal>
  );
};
